import {
  Grid,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  Button, Checkbox, FormControlLabel
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import * as React from 'react';
import { VideoPanelProps, } from '../../types/videos';
import { VideoTranscript } from '../../types/videoTranscript';
import Box from '@mui/material/Box';
import { useContext, useEffect, useState } from 'react';
import { TranscriptV2Panel } from './TranscriptV2Panel';
import { ShortVideo } from './ShortVideo';
import axios from 'axios';
import { getApiBaseUrl } from '../../../utils/env';
import { OrganisationContext } from '../../../App';


const requestMissingTranscript = async (domain: string, uuid: string, lang: string) => {
  if (!lang) {
    return alert(('This advertiser does not have a preferred language set.'));
  }

  await axios.post(`${getApiBaseUrl()}/video-transcript/translate/${domain}`, {
    video_uuid: uuid,
    target_lang: lang
  }).catch((err) => alert(typeof err === 'string' ? err : 'Sorry, this request has' +
    ' failed.'));
};

const toggleDisableCustomCaption =  async (domain: string, uuid: string, newValue: boolean) => {
  return axios.patch(`${getApiBaseUrl()}/video-transcript/disable-custom-caption/${domain}`, {
    video_uuid: uuid,
    disable_custom_caption: newValue
  })
}

export const PanelVideoWithTranscriptV2 = ({ video, availableLangs, readOnlyDisableCustomCaptionToggle }: VideoPanelProps) => {
  const [lang, setLang] = useState(video.transcripts[0]?.lang_code || 'en');
  const [selectedMissingLang, setSelectedMissingLang] = useState('');
  const [transcript, setTranscript] = useState<VideoTranscript>(video.transcripts.find(t => t.lang_code === lang) || video.transcripts[0]);
  const { selectedOrganisation } = useContext(OrganisationContext);
  const [disableCustomCaption, setDisableCustomCaption] = useState(video.disable_custom_caption);

  useEffect(() => {
    const selected = video.transcripts.find(t => t.lang_code === lang);
    if (selected) {
      setTranscript(selected);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const missingTranscriptLangs = availableLangs.filter(lang => !video.transcripts.some(({ lang_code }) => lang_code === lang));

  return (
    <Box sx={{
      p: 2,
      display: 'flex',
      width: '100%',
    }}>
      <Grid container spacing={1} alignContent={'center'}>
        <Grid item xs={6}>
          <Grid container spacing={1} justifyContent={'center'} alignItems={'center'}>
            <ShortVideo transcript={!disableCustomCaption ? video.transcripts.find(x => x.lang_code === lang) : undefined}
                        headline={video.title} media={video.preview_uri || video.media_id}
                        duration={0}/>
          </Grid>
        </Grid>
        <Grid item xs={6}>
          <Grid container flexDirection={'row-reverse'} justifyContent={'space-between'} alignItems={'center'} sx={{
            mb: 2,
          }}>
            {video.transcripts.length > 0 && <Grid item xs={3}>
              <FormControl fullWidth  size={'small'}>
                <InputLabel id="transcript-lang-select-label">Language</InputLabel>
                <Select
                  labelId="transcript-lang-select-label"
                  id="transcript-lang-select"
                  value={lang}
                  label="Language"
                  onChange={(event: SelectChangeEvent) => setLang(event.target.value)}
                >
                  {video.transcripts.map(({ lang_code: lang }, index) => (
                    <MenuItem value={lang} key={lang}>
                      {lang.toUpperCase()} {index === 0 &&
                      <span style={{ paddingLeft: 2 }}>(default)</span>}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>}

            {missingTranscriptLangs.length > 0 && <Grid item xs={6}>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={6}>
                  <FormControl fullWidth size={'small'}>
                    <InputLabel id="missing-transcript-lang-select-label">Missing
                      Language</InputLabel>
                    <Select
                      labelId="missing-transcript-lang-select-label"
                      id="missing-transcript-lang-select"
                      value={selectedMissingLang || ''}
                      label="Missing Language"
                      onChange={(event: SelectChangeEvent) => setSelectedMissingLang(event.target.value)}
                    >
                      {missingTranscriptLangs.map((lang) => (
                        <MenuItem value={lang} key={lang}>
                          {lang.toUpperCase()}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item>
                  <Button
                    startIcon={<AddIcon/>}
                    variant="contained"
                    color="primary"
                    disabled={!selectedMissingLang}
                    onClick={async () => {
                      await requestMissingTranscript(selectedOrganisation as string, video.uuid, selectedMissingLang);
                      setSelectedMissingLang('');
                    }}
                  >
                    Request
                  </Button>
                </Grid>
              </Grid>
            </Grid>}
            <Grid item xs={missingTranscriptLangs.length > 0 ? 12 : 6}><Typography sx={{
              fontSize: '16px',
              fontWeight: 'bold',
              mt: 3,
              mb: 1
            }}>Transcript</Typography></Grid>
          </Grid>

          {transcript && <TranscriptV2Panel transcript={transcript}/>}
          <Grid item xs={12} data-test={disableCustomCaption}>
              <FormControlLabel
                sx={{ marginRight: 0 }}
                control={<Checkbox checked={disableCustomCaption} onChange={ async (e) => {
                  await toggleDisableCustomCaption(selectedOrganisation as string, video.uuid, e.target.checked);
                  setDisableCustomCaption(!e.target.checked);
                }} />}
                disabled={readOnlyDisableCustomCaptionToggle}
                label="Disable custom caption"
              />
            </Grid>
        </Grid>
      </Grid>
    </Box>

  );
};


